/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import Immutable from 'immutable';
import styled from 'styled-components';
import memoize from 'hs-lodash/memoize';
import { CALYPSO, OLAF, GREAT_WHITE, CALYPSO_DARK } from 'HubStyleTokens/colors';
import { BUTTON_RADIUS } from 'HubStyleTokens/sizes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import Big from 'UIComponents/elements/Big';
import UIDropdownCaret from 'UIComponents/dropdown/UIDropdownCaret';
import AbstractTextElement from 'UIComponents/elements/AbstractTextElement';
import UISearchInput from 'UIComponents/input/UISearchInput';
import UISelect from 'UIComponents/input/UISelect';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import * as GROUPED_CATEGORIES from 'ecosystem-marketplace-core/constants/groupTags/SuperCategoryGroup';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import MediaQuery from 'ecosystem-marketplace-core/components/layout/MediaQuery';
import { getSuperCategoryI18nKey, getCategoryI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import superCategoryOrder from 'ecosystem-marketplace-core/constants/groupTags/superCategoryOrder';
import StorefrontUsageTrackerContext from '../../../context/StorefrontUsageTrackerContext';
import SearchSelectContext from './SearchSelectContext';
const ALL_CATEGORIES_OPTION_VALUE = '__ALL_CATEGORIES__';
const SearchSelectInputWrapper = styled.div.withConfig({
  displayName: "SearchSelectInput__SearchSelectInputWrapper",
  componentId: "lbww0u-0"
})(["background-color:", ";border-radius:", ";"], OLAF, BUTTON_RADIUS);
const CategorySelectBox = styled(UIBox).withConfig({
  displayName: "SearchSelectInput__CategorySelectBox",
  componentId: "lbww0u-1"
})(["display:flex;align-items:stretch;border-right:1px solid ", ";&& .category-dropdown-button{padding-right:20px;padding-left:20px;&:hover{text-decoration:none;}}"], GREAT_WHITE);
const CategorySelectCaret = styled(props => {
  return /*#__PURE__*/_jsx(Big, {
    children: /*#__PURE__*/_jsx(UIDropdownCaret, Object.assign({}, props))
  });
}).withConfig({
  displayName: "SearchSelectInput__CategorySelectCaret",
  componentId: "lbww0u-2"
})(["color:", ";&:hover{color:", ";}"], CALYPSO, CALYPSO_DARK);
const CategorySelect = styled(UISelect).withConfig({
  displayName: "SearchSelectInput__CategorySelect",
  componentId: "lbww0u-3"
})(["height:100%;display:flex !important;align-items:center !important;padding:0 20px;"]);

const CategorySelectValue = ({
  children
}) => /*#__PURE__*/_jsx(Big, {
  children: /*#__PURE__*/_jsx("b", {
    children: children
  })
});

const getI18nKey = key => `appMarketplace.storefront.searchSelect.${key}`;

const getCategorySelectPlaceholder = () => {
  return unescapedText(getI18nKey('searchCategorySelectPlaceholder'));
};

const getCategorySelectValue = (superCategory, category) => {
  if (superCategory && category) {
    return `${superCategory}:${category}`;
  } else if (superCategory) {
    return superCategory;
  }

  return ALL_CATEGORIES_OPTION_VALUE;
};

const getCategorySelectOptions = memoize(showHelpText => {
  const options = superCategoryOrder.reduce((final, superCategory) => {
    const categories = Immutable.Set(GROUPED_CATEGORIES[superCategory]);
    const superCategoryText = unescapedText(getSuperCategoryI18nKey(superCategory));
    final.push({
      text: superCategoryText,
      value: superCategory,
      dropdownText: /*#__PURE__*/_jsx(AbstractTextElement, {
        weight: "demi",
        children: superCategoryText
      })
    });
    return final.concat(categories.map(category => [category, unescapedText(getCategoryI18nKey(category))]).sortBy(([, categoryText]) => categoryText).map(([category, categoryText]) => ({
      text: categoryText,
      value: getCategorySelectValue(superCategory, category),
      dropdownClassName: 'p-left-4',
      help: showHelpText ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('inCategory'),
        options: {
          category: superCategoryText
        }
      }) : null
    })).toArray());
  }, []);
  const placeholder = getCategorySelectPlaceholder();
  return [{
    text: placeholder,
    dropdownText: /*#__PURE__*/_jsx(AbstractTextElement, {
      weight: "demi",
      children: placeholder
    }),
    value: ALL_CATEGORIES_OPTION_VALUE
  }, ...options];
});

class SearchSelectInput extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      categoryInputValue: '',
      categorySelectOpen: false
    };

    this.handleCategoryInputValueChange = evt => {
      this.setState({
        categoryInputValue: evt.target.value
      });
    };

    this.handleCategorySelectOpenChange = evt => {
      this.setState({
        categorySelectOpen: evt.target.value,
        categoryInputValue: ''
      });
    };
  }

  render() {
    const {
      categoryInputValue,
      categorySelectOpen
    } = this.state;
    return /*#__PURE__*/_jsx(SearchSelectContext.Consumer, {
      children: ({
        superCategory,
        category,
        size,
        readOnly,
        updateSearchFilters
      }) => /*#__PURE__*/_jsx(StorefrontUsageTrackerContext.Consumer, {
        children: ({
          trackInteraction
        }) => /*#__PURE__*/_jsx(SearchSelectInputWrapper, {
          children: /*#__PURE__*/_jsxs(UIFlex, {
            align: "stretch",
            children: [/*#__PURE__*/_jsx(CategorySelectBox, {
              grow: 0,
              shrink: 0,
              children: /*#__PURE__*/_jsx(MediaQuery, {
                minScreenSize: ScreenSizes.SM,
                children: /*#__PURE__*/_jsx(UIFlex, {
                  direction: "column",
                  justify: "center",
                  style: {
                    height: '100%'
                  },
                  children: /*#__PURE__*/_jsx(CategorySelect, {
                    anchorType: "button",
                    buttonUse: "primary-white",
                    caretRenderer: CategorySelectCaret,
                    filterOption: ({
                      text,
                      value
                    }, input) => {
                      if (!input) {
                        return true;
                      }

                      return value !== ALL_CATEGORIES_OPTION_VALUE && !!~UISelect.stripDiacritics(text.toLowerCase()).indexOf(input);
                    },
                    inputValue: categoryInputValue,
                    itemComponent: "div",
                    menuWidth: 250,
                    open: categorySelectOpen,
                    onChange: ({
                      target: {
                        value
                      }
                    }) => {
                      if (value === ALL_CATEGORIES_OPTION_VALUE) {
                        updateSearchFilters();
                      } else {
                        const [optionSuperCategory, optionCategory] = value.split(':');
                        updateSearchFilters(optionSuperCategory, optionCategory);
                      }

                      trackInteraction('select search category', {
                        value
                      }, 'storefront-select-search-category');
                    },
                    onInputValueChange: this.handleCategoryInputValueChange,
                    onOpenChange: this.handleCategorySelectOpenChange,
                    options: getCategorySelectOptions(!!categoryInputValue),
                    placeholder: getCategorySelectPlaceholder(),
                    readOnly: readOnly,
                    searchable: true,
                    value: getCategorySelectValue(superCategory, category),
                    valueComponent: CategorySelectValue
                  })
                })
              })
            }), /*#__PURE__*/_jsx(UISearchInput, Object.assign({}, this.props, {
              "data-selenium-id": "storefront-search-input",
              autoComplete: "off",
              placeholder: unescapedText(getI18nKey('searchInputPlaceholder')),
              size: size,
              styled: false
            }))]
          })
        })
      })
    });
  }

}

export default SearchSelectInput;